import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Users, TrendingUp, Wallet, Plus, ArrowRight, Sparkles, Activity } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import MilestoneCelebration from '@/components/MilestoneCelebration';
import AnimatedBackground from '@/components/AnimatedBackground';
import FloatingMoneyIcons from '@/components/FloatingMoneyIcons';
import ShareInviteCard from '@/components/ShareInviteCard';
import QRCodeShare from '@/components/QRCodeShare';
import { BadgeProgressCard } from '@/components/BadgeSystem';
import { MILESTONES } from '@/components/ConfettiEffect';
import ProtectionRewards from '@/components/ProtectionRewards';
import { GlobalGuardianLeaderboard } from '@/components/GuardianLeaderboard';
import ReferralProgram from '@/components/ReferralProgram';
import SendMoney from '@/components/SendMoney';
import ProtectionAlerts from '@/components/ProtectionAlerts';
import MiniBankDashboard from '@/components/MiniBankDashboard';
import BankAccountManager from '@/components/BankAccountManager';
import TrustBadge from '@/components/TrustBadge';
import YieldCard from '@/components/YieldCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState(null);

  useEffect(() => {
    loadDashboard();
    
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadDashboard = async () => {
    try {
      const [statsRes, clubsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/clubs`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setClubs(clubsRes.data.slice(0, 3));
      
      // Check for new milestones
      if (statsRes.data.new_milestones && statsRes.data.new_milestones.length > 0) {
        const highestMilestone = Math.max(...statsRes.data.new_milestones);
        const milestoneData = MILESTONES.find(m => m.amount === highestMilestone);
        
        if (milestoneData) {
          const nextMilestone = MILESTONES.find(m => m.amount > highestMilestone);
          setCurrentMilestone({
            ...milestoneData,
            nextMilestone: nextMilestone?.amount
          });
          setShowMilestone(true);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Dashboard load error:', error);
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your contribution has been recorded.');
          navigate('/dashboard', { replace: true });
          loadDashboard();
          return;
        }

        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment processing... Please check back shortly.');
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-lime-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden font-sans selection:bg-lime-500/30">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      <AnimatedBackground />
      <FloatingMoneyIcons />
      
      <div className="relative z-10">
        <Navbar />
        
        {/* Live Activity Ticker Mock */}
        <div className="border-b border-white/5 bg-[#0A0A0A]">
          <div className="relative flex overflow-x-hidden border-y border-white/5 bg-white/[0.02]">
            <div className="py-2 animate-marquee whitespace-nowrap flex items-center">
              {[1, 2, 3, 4, 5].map((idx) => (
                <div key={idx} className="flex items-center mx-8 text-xs font-bold uppercase tracking-widest text-neutral-400">
                  <Activity className="w-3 h-3 mr-2 text-lime-400 animate-pulse" />
                  <span>Today's Live Global Contributions: <span className="text-lime-400">$24,520</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <MilestoneCelebration
          isOpen={showMilestone}
          milestone={currentMilestone}
          onClose={() => setShowMilestone(false)}
        />
        
        <main className="max-w-7xl mx-auto px-6 py-12">
          {checkingPayment && (
            <Card className="mb-6 border-lime-500/30 bg-lime-500/5">
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <Loader2 className="w-5 h-5 text-lime-500 animate-spin mr-3" />
                  <span className="text-lime-100 font-bold">Verifying your payment...</span>
                </div>
              </CardContent>
            </Card>
          )}

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="mb-12">
              <h1 className="text-4xl md:text-5xl font-black text-white mb-3 flex items-center tracking-tight uppercase" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Welcome back 
                <motion.span
                  animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
                  transition={{ duration: 2, delay: 0.5 }}
                  className="ml-3 inline-block origin-bottom-right"
                >
                  👋
                </motion.span>
              </h1>
              <div className="flex items-center gap-4 flex-wrap">
                <p className="text-neutral-400 font-medium">Here's your live arena overview</p>
                <div className="h-6 w-px bg-white/10 hidden sm:block"></div>
                <TrustBadge size="sm" data-testid="dashboard-trust-badge" />
              </div>
            </motion.div>

            {/* Stat Scoreboards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                {
                  id: "stat-total-savings",
                  title: "Total Savings",
                  value: stats?.total_savings?.toFixed(2) || '0.00',
                  subtitle: "Across all clubs",
                  icon: Wallet,
                  color: "text-lime-400",
                  bg: "bg-lime-500/10",
                  glow: "group-hover:shadow-[0_0_30px_rgba(132,204,22,0.2)]"
                },
                {
                  id: "stat-total-clubs",
                  title: "My Clubs",
                  value: stats?.total_clubs || 0,
                  subtitle: "Active memberships",
                  icon: Users,
                  color: "text-cyan-400",
                  bg: "bg-cyan-400/10",
                  glow: "group-hover:shadow-[0_0_30px_rgba(34,211,238,0.2)]"
                },
                {
                  id: "stat-total-invested",
                  title: "Total Invested",
                  value: stats?.total_invested?.toFixed(2) || '0.00',
                  subtitle: "Active investments",
                  icon: TrendingUp,
                  color: "text-purple-400",
                  bg: "bg-purple-400/10",
                  glow: "group-hover:shadow-[0_0_30px_rgba(192,132,252,0.2)]"
                },
                {
                  id: "stat-total-earnings",
                  title: "Interest Earned",
                  value: stats?.total_earnings?.toFixed(2) || '0.00',
                  subtitle: "Lifetime earnings",
                  icon: Sparkles,
                  color: "text-amber-400",
                  bg: "bg-amber-400/10",
                  glow: "group-hover:shadow-[0_0_30px_rgba(251,191,36,0.2)]"
                }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                >
                  <Card 
                    data-testid={stat.id} 
                    className={`stat-card border-white/10 bg-[#111111] transition-all group overflow-hidden relative rounded-3xl ${stat.glow}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none"></div>
                    <CardHeader className="pb-2 border-b border-white/5 relative z-10">
                      <CardTitle className="text-xs font-bold uppercase tracking-widest text-neutral-500">
                        {stat.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 relative z-10">
                      <div className="flex items-center justify-between">
                        <div>
                          <motion.div
                            className={`text-3xl font-black tracking-tighter ${stat.color}`}
                            style={{ fontFamily: 'Unbounded, monospace' }}
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 200, delay: 0.2 + (idx * 0.1) }}
                          >
                            {stat.id.includes('total-clubs') ? '' : '$'}{stat.value}
                          </motion.div>
                          <p className="text-xs text-neutral-500 mt-1 font-bold">{stat.subtitle}</p>
                        </div>
                        <div className={`w-12 h-12 rounded-2xl ${stat.bg} flex items-center justify-center border border-white/5`}>
                          <stat.icon className={`w-6 h-6 ${stat.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              <motion.div variants={itemVariants}>
                <Card className="border-white/10 bg-[#111111] rounded-3xl h-full flex flex-col">
                  <CardHeader className="border-b border-white/5 pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        My Active Clubs
                      </CardTitle>
                      <Button
                        data-testid="view-all-clubs-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/clubs')}
                        className="rounded-full hover:bg-white/10 hover:text-white text-neutral-400 text-xs font-bold"
                      >
                        View All
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow flex flex-col pt-6">
                    {clubs.length === 0 ? (
                      <div className="text-center py-12 flex-grow flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-2xl m-2">
                        <Users className="w-12 h-12 text-neutral-600 mb-4" />
                        <p className="text-neutral-400 mb-6 font-medium">You haven't joined any clubs yet.</p>
                        <Button
                          data-testid="browse-clubs-button"
                          onClick={() => navigate('/clubs')}
                          className="rounded-full bg-lime-500 hover:bg-lime-400 text-black font-black"
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Browse Clubs
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {clubs.map((club, idx) => (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + (idx * 0.1) }}
                            key={club.club_id}
                            data-testid={`club-card-${club.club_id}`}
                            className="p-4 bg-[#1A1A1A] border border-white/5 rounded-2xl hover:border-lime-500/30 hover:shadow-[0_0_20px_rgba(132,204,22,0.1)] transition-all cursor-pointer group"
                            onClick={() => navigate(`/clubs/${club.club_id}`)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-bold text-white group-hover:text-lime-400 transition-colors">{club.name}</h3>
                                <p className="text-sm text-neutral-500 mt-1 font-medium">
                                  ${club.contribution_amount} / {club.contribution_frequency}
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-black text-white" style={{ fontFamily: 'Unbounded, monospace' }}>
                                  <span className="text-neutral-500 text-xs mr-1">Pool</span>
                                  ${club.total_pool.toFixed(2)}
                                </div>
                                <div className="text-xs text-neutral-500 font-bold mt-1">
                                  <Users className="w-3 h-3 inline mr-1" />
                                  {club.members.length} members
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card className="border-white/10 bg-[#111111] rounded-3xl h-full flex flex-col">
                  <CardHeader className="border-b border-white/5 pb-4">
                    <CardTitle className="text-lg font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      Command Center
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <Button
                        data-testid="create-club-button"
                        onClick={() => navigate('/clubs?action=create')}
                        className="w-full rounded-2xl py-8 bg-lime-500 hover:bg-lime-400 text-black justify-start border-none group transition-all"
                      >
                        <div className="w-10 h-10 rounded-xl bg-black/10 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                          <Plus className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <div className="font-black text-lg">Create a Club</div>
                          <div className="text-xs font-bold opacity-70">Start a new rotation with friends</div>
                        </div>
                      </Button>
                      <Button
                        data-testid="browse-clubs-action-button"
                        onClick={() => navigate('/clubs')}
                        variant="outline"
                        className="w-full rounded-2xl py-8 border-white/10 hover:bg-white/5 hover:border-cyan-500/50 hover:text-white justify-start bg-[#1A1A1A] group transition-all"
                      >
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                          <Users className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-lg text-white">Browse Clubs</div>
                          <div className="text-xs font-medium text-neutral-500">Join an existing active group</div>
                        </div>
                      </Button>
                      <Button
                        data-testid="investments-button"
                        onClick={() => navigate('/investments')}
                        variant="outline"
                        className="w-full rounded-2xl py-8 border-white/10 hover:bg-white/5 hover:border-purple-500/50 hover:text-white justify-start bg-[#1A1A1A] group transition-all"
                      >
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                          <TrendingUp className="w-5 h-5 text-purple-400" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-lg text-white">Investment Portal</div>
                          <div className="text-xs font-medium text-neutral-500">Grow your accumulated funds</div>
                        </div>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Other Existing Widgets */}
            <motion.div variants={itemVariants}>
              <ProtectionAlerts onReloadClick={(clubId) => navigate(`/clubs/${clubId}`)} />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <ReferralProgram />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <MiniBankDashboard 
                onTransferClick={() => {
                  const sendMoneySection = document.getElementById('send-money-section');
                  if (sendMoneySection) {
                    sendMoneySection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <YieldCard />
            </motion.div>

            <motion.div id="send-money-section" variants={itemVariants} className="mt-8">
              <SendMoney />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <BankAccountManager />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <ShareInviteCard />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <ProtectionRewards compact={true} />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <BadgeProgressCard totalSavings={stats?.total_savings || 0} />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <GlobalGuardianLeaderboard />
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <QRCodeShare variant="card" size="md" data-testid="dashboard-qr-card" />
            </motion.div>

          </motion.div>
        </main>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}} />
    </div>
  );
}
